/**
 * Nœud principal : QCM (consigne + choix) + pêche aux indices (inférence) +
 * bouton « j'ai besoin d'aide » (chemin développé on_request). Mobile-first :
 * choix en boutons empilés `w-full`, action primaire `w-full`, touch ≥ 44px.
 */
import { useState } from 'react';
import { Button, cn, MarkdownMath } from '@jeraisonne/ui';
import { HelpCircle } from 'lucide-react';
import {
  AnswerDock,
  AnswerKeys,
  PaperChoiceList,
  choiceKeyLayout,
  type PaperQcm,
} from '@jeraisonne/ui/paper';
import { IndiceFisher } from './indice-fisher';
import type { PlayerChoix, PlayerIndice, PlayerNoeud } from './types';

export interface NoeudSubmitArgs {
  choixId: string | null;
  fished: string[];
  requestSubnode: boolean;
}

export function NoeudView({
  node,
  choix,
  indices,
  allowHelp,
  submitting,
  onSubmit,
  paper = false,
}: {
  node: PlayerNoeud;
  choix: PlayerChoix[];
  indices: PlayerIndice[];
  allowHelp: boolean;
  submitting: boolean;
  onSubmit: (args: NoeudSubmitArgs) => void;
  /** exercice « sur la feuille » : touches dans le dock du bas + « Check » */
  paper?: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [fished, setFished] = useState<Set<string>>(new Set());
  const showFisher = node.kind === 'inference' && indices.length > 0;

  function toggleFished(id: string) {
    setFished((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (paper) {
    // le serveur juge (submit-dag-answer) : les touches ne se colorent pas ici
    const options = choix.map((c) => ({
      id: c.id,
      label: c.label,
      visuals: [],
      is_correct: false,
    }));
    const qcm: PaperQcm = {
      options,
      multiple: false,
      layout: choiceKeyLayout(
        options.map((o) => ({ id: o.id, label: o.label, hasVisuals: false })),
      ),
      selected: selected ? [selected] : [],
      chosen: [],
      revealed: false,
      shown: true,
      busy: submitting,
      toggle: (id) => setSelected((s) => (s === id ? null : id)),
      check: () => {
        if (!selected || submitting) return;
        onSubmit({ choixId: selected, fished: [...fished], requestSubnode: false });
      },
    };
    return (
      <div className="space-y-4">
        <p className="text-ink-900 text-base font-semibold leading-snug sm:text-lg">
          <MarkdownMath>{node.enonce_qcm ?? 'Quelle est la prochaine étape ?'}</MarkdownMath>
        </p>
        {showFisher && <IndiceFisher indices={indices} selected={fished} onToggle={toggleFished} />}
        <PaperChoiceList qcm={qcm} />
        {allowHelp && (
          <button
            type="button"
            disabled={submitting}
            onClick={() => onSubmit({ choixId: null, fished: [...fished], requestSubnode: true })}
            className="text-ink-600 flex min-h-[2.75rem] items-center gap-1.5 text-sm font-medium active:scale-[0.99] disabled:opacity-50"
          >
            <HelpCircle className="h-4 w-4" />
            J&apos;ai besoin d&apos;aide
          </button>
        )}
        <AnswerDock label="Ta réponse">
          <AnswerKeys qcm={qcm} />
        </AnswerDock>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-ink-900 text-base font-semibold leading-snug sm:text-lg">
        <MarkdownMath>{node.enonce_qcm ?? 'Quelle est la prochaine étape ?'}</MarkdownMath>
      </p>

      {showFisher && <IndiceFisher indices={indices} selected={fished} onToggle={toggleFished} />}

      <ul className="space-y-2">
        {choix.map((c) => {
          const isSel = selected === c.id;
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => setSelected(c.id)}
                aria-pressed={isSel}
                className={cn(
                  'flex min-h-[2.75rem] w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm active:scale-[0.99]',
                  isSel
                    ? 'border-primary-400 bg-primary-50 text-primary-800 ring-primary-200 ring-2'
                    : 'border-ink-200 text-ink-800 bg-white',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 h-4 w-4 shrink-0 rounded-full border',
                    isSel ? 'border-primary-500 bg-primary-500' : 'border-ink-300',
                  )}
                />
                <span className="flex-1">
                  <MarkdownMath>{c.label}</MarkdownMath>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="space-y-2">
        <Button
          fullWidth
          disabled={submitting || !selected}
          onClick={() =>
            onSubmit({ choixId: selected, fished: [...fished], requestSubnode: false })
          }
        >
          {submitting ? 'Validation…' : 'Valider'}
        </Button>
        {allowHelp && (
          <button
            type="button"
            disabled={submitting}
            onClick={() => onSubmit({ choixId: null, fished: [...fished], requestSubnode: true })}
            className="text-ink-600 flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-xl text-sm font-medium active:scale-[0.99] disabled:opacity-50"
          >
            <HelpCircle className="h-4 w-4" />
            J&apos;ai besoin d&apos;aide
          </button>
        )}
      </div>
    </div>
  );
}
